import { useTimeoutFn, useIntervalFn, useThrottleFn, useDateFormat } from '@vueuse/core'
import OSS from 'ali-oss'
import router from "@/router";
import { ElMessage, ElMessageBox, UploadRawFile } from 'element-plus'
import { Base64 } from 'js-base64'
import { isObject } from '@vue/shared'
import { cloneDeep, debounce } from 'lodash-es'
import { requestWy } from '@/utils/request'
import { getEnumdefApi, exportExcelApi } from '@/api/newPage/Common'
import { EnumTypeList } from '@/api/newPage/Common/types'
import { store } from '@/pinia/store'
import moment from 'moment'

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

export const useGlobalSetTimeout = (fn: () => void, time = 1000) => {
  useTimeoutFn(fn, time)
}

export const useGlobalSetInterval = (fn: () => void, time = 1000) => {
  useIntervalFn(fn, time)
}

// 节流函数，可用于放重复点击
export const useGlobalThrottle = (callback: () => void, time = 2000) =>
  useThrottleFn(() => {
    callback()
  }, time)

/**
 * @param {Blob} data 文件流
 * @param {string} fileName 文件名称
 * @param {string} mineType 文件格式
 */
const downloadFun = (data: Blob, fileName: string, mineType: string) => {
  console.log(data)
  // 创建 blob
  const blob = new Blob([data], { type: mineType })
  // 创建 href 超链接，点击进行下载
  window.URL = window.URL || window.webkitURL
  const href = URL.createObjectURL(blob)
  const downA = document.createElement('a')
  downA.href = href
  downA.download = fileName
  downA.click()
  // 销毁超连接
  window.URL.revokeObjectURL(href)
  ElMessage({
    message: `下载成功~`,
    type: 'success'
  })
}

// 下载文件流
export const downloadBlob = {
  // 下载 Excel 方法
  excel: (data: Blob, fileName: string) => {
    downloadFun(data, fileName, 'application/vnd.ms-excel')
  },
  // 下载 Word 方法
  word: (data: Blob, fileName: string) => {
    downloadFun(data, fileName, 'application/msword')
  },
  // 下载 Zip 方法
  zip: (data: Blob, fileName: string) => {
    downloadFun(data, fileName, 'application/zip')
  },
  // 下载 Html 方法
  html: (data: Blob, fileName: string) => {
    downloadFun(data, fileName, 'text/html')
  },
  // 下载 Markdown 方法
  markdown: (data: Blob, fileName: string) => {
    downloadFun(data, fileName, 'text/markdown')
  }
}

export const resetObj = (newObj: Object, reactiveObj: Object, isClean = false) => {
  //reactive对象循环添加属性
  // isClean:是否清空旧对象
  if (isClean) {
    for (const key in reactiveObj) {
      delete reactiveObj[key]
    }
  }

  for (const key in newObj) {
    reactiveObj[key] = newObj[key]
  }
}

export const resetArr = (newArr: any[], reactiveArr: any[]) => {
  //reactive数组循环添加属性
  reactiveArr.length = 0
  newArr?.forEach((item) => {
    reactiveArr.push(item)
  })
}

/**
 * getDialogWidth
 * @param type 弹窗宽度类型(small:'375',default:'450',large:'600')
 * @returns 弹窗宽度:string
 * @description  获取弹出层宽度，统一设置
 */
export const getDialogWidth = (type:  string = 'large') => {
  // 获取弹出层宽度，统一设置
  const widthObj = {
    small: '375',
    default: '450',
    large: '680'
  }
  if (window.innerWidth < 720) {
    const w = window.innerWidth
    return w > 290 ? w - 30 : 350
  }
  return widthObj[type]||type
}

export const isTableFixed = (direction: 'right' | 'left' = 'right') => {
  // 表格对应内容(右侧操作栏)是否要固定
  if (window.innerWidth < 720) {
    return false
  }
  return direction
}

/**
 *
 * @param  relativePic 视频地址
 * @returns
 */
export const showVideoCoverUrl = (relativePic: string) => {
  if (!relativePic) {
    return ''
  }
  return `${relativePic}?x-oss-process=video/snapshot,t_1000,f_jpg,w_0,h_0,m_fast,ar_auto`
}



// 数字转换
export const numFormat = (val: number) => {
  const total =
    val.toString().indexOf('.') != -1 ? parseFloat(val.toString()).toFixed(2) : val.toFixed(2)
  return total
}

export const showImageUrl = (pic: string) => {
  if (!pic) {
    return ''
  }
  return pic.includes('://oss') ? `${pic}!zip50` : pic
}

/**
 * 获取时间选择框额外配置，全局统一设置
 * @returns
 */
export const timeShortcuts = () => {
  return [
    {
      text: '上一周',
      value: () => {
        const end = new Date()
        const start = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        return [start, end]
      }
    },
    {
      text: '上一个月',
      value: () => {
        const end = new Date()
        const start = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
        return [start, end]
      }
    },
    {
      text: '前3个月',
      value: () => {
        const end = new Date()
        const start = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
        return [start, end]
      }
    }
  ]
}

// 三位间隔
export const transformNum = (val: string | number | null | undefined) => {
  if (!val) return 0
  const num = val + ''
  const x = num.split('.')
  let x1 = x[0]
  const x2 = x.length > 1 ? '.' + x[1] : ''
  const reg = /(\d+)(\d{3})/
  while (reg.test(x1)) {
    x1 = x1.replace(reg, '$1' + ',' + '$2')
  }
  return x1 + x2
}

// 数字转换
export const resetNumber = (val: string | number) => {
  if (!val) return '0'
  const num = val + ''
  return transformNum(num)
}

// 金额转换
export const resetAmount = (val: string | number | null | undefined) => {
  if (!val) return '0.00'
  const amount = parseFloat(val.toString()).toFixed(2) + ''
  return transformNum(amount)
}


// 获取url参数
export const getUrlData = (name: string) => {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  const r = (
    decodeURIComponent(window.location.search) ||
    decodeURIComponent(window.location.hash).substr(
      decodeURIComponent(window.location.hash).indexOf('?')
    )
  )
    .substr(1)
    .match(reg)
  if (r != null) return unescape(r[2])
  return null
}

export const push = (path = "/", query: GlobalUrlQueryType = {}) => {
  let queryClone = { ...query };
  const timeTemp = getUrlData("timeTemp");
  if (timeTemp) {
    queryClone.timeTemp = timeTemp;
  }
  router.push({
    path,
    query: queryClone,
  });
};

// 校验手机号码是否填写正确
export const regTel = (val: string) => {
  if (!val) return false
  const reg = /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/
  return reg.test(val)
}

/**
 * 复制文本
 * @param {String} value 需要复制的文本
 */
export const copyText = async (value: string) => {
  if (!value) return ElMessage.warning('无复制内容！')
  // 判断当前环境是否支持navigator.clipboard 对象
  try {
    if (!!navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        ElMessage.success('复制成功！')
      } else {
        fallbackCopyText(value);
      }
    } catch (err) {
      fallbackCopyText(value);
    }
}

const fallbackCopyText = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      textArea.select();
      textArea.setSelectionRange(0, 99999);
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        ElMessage.success('复制成功！')
      } else {
        ElMessage.error('复制失败！')
      }
    } catch (err) {
      ElMessage.error('复制失败！')
    }
  };

// 获取selectOption的label
export const getOptionsLabel = (val: string | number, options: SelectOptionType) => {
  if (val === '') return ''
  const obj = options.find((x) => x.value.toString() === val.toString())
  return (obj ? obj.label : '') as string
}

// 时间格式转换
export const resetTimeValue = (time: string) => {
  if (!time) return ''
  const timeStr = time.split(':')
  const newDate = new Date()
  newDate.setHours(parseInt(timeStr[0]))
  newDate.setMinutes(parseInt(timeStr[1]))
  return newDate
}

// 判断按钮权限
export const hasPermission = (name: string) => {
  const permissions = localStorage.getItem('permissions')
  if (permissions && permissions.indexOf(name) > -1) {
    return true;
  } else {
    return false;
  }
}

export const ossUpload = async (authToken: any, file: UploadRawFile) => {
  const auth = JSON.parse(Base64.decode(authToken.data.uploadAuth))
  const address = JSON.parse(Base64.decode(authToken.data.uploadAddress))
  console.log('auth解密', auth)
  console.log('address解密', address)

  const client = new OSS({
    region: 'oss-' + auth.Region,
    accessKeyId: auth.AccessKeyId,
    accessKeySecret: auth.AccessKeySecret,
    bucket: address.Bucket,
    stsToken: auth.SecurityToken,
    secure: true
  })
  const res = await client.put(authToken.data.path, file)
  console.log('上传返回结果', res)
  if (res && res.url) {
    ElMessage({
      type: 'success',
      message: '图片上传成功'
    })
  } else {
    ElMessage({
      type: 'warning',
      message: '图片上传失败'
    })
  }
}

/**
 * @param  stamp 需要转换的原时间
 * @param  type 要转换的格式
 * @returns
 */
const dateFormat = (stamp: any, type = 1) => {
  if (!stamp) {
    return "";
  }
  const obj = {
    1: "YYYY-MM-DD HH:mm:ss",
    2: "YYYY-MM-DD HH:mm",
    3: "YYYY-MM-DD",
    4: "HH:mm:ss",
    5: "MM-DD",
    6: "MM.DD",
    7: "HH:mm",
    8: "MM-DD HH:mm",
    9: "YYYYMMDDHHmmss",
    10: "YYYY-MM",
    11: "YYYY-MM-01",
  };
  const stampStr = stamp + "";
  let time = new Date(stamp).getTime();
  if (stampStr.indexOf("T") > -1) {
    const stampFilter = stamp
      .substring(0, stampStr.indexOf("."))
      .replace("T", " ")
      .replace(/-/g, "/");
    time = new Date(stampFilter).getTime() + 1000 * 60 * 60 * 8;
  }
  return useDateFormat(time, obj[type]).value;
};

//版本控制，刷新JS
export const configVar = () => ({
  version: (null === localStorage.getItem("vknock") || 'null' === localStorage.getItem("vknock")) ? dateFormat(new Date(), 3) : localStorage.getItem("vknock") //'0.0.1'
})


/**
 * @description 添加单位
 * @param {String | Number} value 值 100
 * @param {String} unit 单位 px em rem
 */
export const addUnit = (value: string | number, unit = 'px') => {
  return !Object.is(Number(value), NaN) ? `${value}${unit}` : value
}

/**
* @description 添加单位
* @param {unknown} value
* @return {Boolean}
*/
export const isEmpty = (value: unknown) => {
  return value == null && typeof value == 'undefined'
}

/**
* @description 数组扁平化
* @param arr { Array } 扁平化对象
* @return { Array } 扁平化后的数组
*/
export const flatten = (arr: any[]): any[] => {
  return arr.reduce((result, item) => {
    return result.concat(Array.isArray(item) ? flatten(item) : item)
  }, [])
}

/**
* @description 树转数组，队列实现广度优先遍历
* @param {Array} data  数据
* @param {Object} props `{ children: 'children' }`
*/

export const treeToArray = (data: any[], props = { children: 'children' }) => {
  data = cloneDeep(data)
  const { children } = props
  const newData = []
  const queue: any[] = []
  data.forEach((child: any) => queue.push(child))
  while (queue.length) {
    const item: any = queue.shift()
    if (item[children]) {
      item[children].forEach((child: any) => queue.push(child))
      delete item[children]
    }
    newData.push(item)
  }
  return newData
}

/**
* @description 数组转
* @param {Array} data  数据
* @param {Object} props `{ parent: 'pid', children: 'children' }`
*/

export const arrayToTree = (
  data: any[],
  props = { id: 'id', parentId: 'pid', children: 'children' }
) => {
  data = cloneDeep(data)
  const { id, parentId, children } = props
  const result: any[] = []
  const map = new Map()
  data.forEach((item) => {
    map.set(item[id], item)
    const parent = map.get(item[parentId])
    if (parent) {
      parent[children] = parent[children] ?? []
      parent[children].push(item)
    } else {
      result.push(item)
    }
  })
  return result
}

/**
* @description 获取正确的路经
* @param {String} path  数据
*/
export function getNormalPath(path: string) {
  if (path.length === 0 || !path || path == 'undefined') {
    return path
  }
  const newPath = path.replace('//', '/')
  const length = newPath.length
  if (newPath[length - 1] === '/') {
    return newPath.slice(0, length - 1)
  }
  return newPath
}

/**
* @description对象格式化为Query语法
* @param { Object } params
* @return {string} Query语法
*/
export function objectToQuery(params: Record<string, any>): string {
  let query = ''
  for (const props of Object.keys(params)) {
    const value = params[props]
    const part = encodeURIComponent(props) + '='
    if (!isEmpty(value)) {
      if (isObject(value)) {
        for (const key of Object.keys(value)) {
          if (!isEmpty(value[key])) {
            const params = props + '[' + key + ']'
            const subPart = encodeURIComponent(params) + '='
            query += subPart + encodeURIComponent(value[key]) + '&'
          }
        }
      } else {
        query += part + encodeURIComponent(value) + '&'
      }
    }
  }
  return query.slice(0, -1)
}

/**
* @description 时间格式化
* @param dateTime { number } 时间戳
* @param fmt { string } 时间格式
* @return { string }
*/
// yyyy:mm:dd|yyyy:mm|yyyy年mm月dd日|yyyy年mm月dd日 hh时MM分等,可自定义组合
export const timeFormat = (dateTime: number, fmt = 'yyyy-mm-dd') => {
  // 如果为null,则格式化当前时间
  if (!dateTime) {
    dateTime = Number(new Date())
  }
  // 如果dateTime长度为10或者13，则为秒和毫秒的时间戳，如果超过13位，则为其他的时间格式
  if (dateTime.toString().length == 10) {
    dateTime *= 1000
  }
  const date = new Date(dateTime)
  let ret
  const opt: any = {
    'y+': date.getFullYear().toString(), // 年
    'm+': (date.getMonth() + 1).toString(), // 月
    'd+': date.getDate().toString(), // 日
    'h+': date.getHours().toString(), // 时
    'M+': date.getMinutes().toString(), // 分
    's+': date.getSeconds().toString() // 秒
  }
  for (const k in opt) {
    ret = new RegExp('(' + k + ')').exec(fmt)
    if (ret) {
      fmt = fmt.replace(
        ret[1],
        ret[1].length == 1 ? opt[k] : opt[k].padStart(ret[1].length, '0')
      )
    }
  }
  return fmt
}

/**
* @description 获取不重复的id
* @param length { Number } id的长度
* @return { String } id
*/
export const getNonDuplicateID = (length = 8) => {
  let idStr = Date.now().toString(36)
  idStr += Math.random().toString(36).substring(3, length)
  return idStr
}

/**
* @description 单词首字母大写
* @param  { String } str
* @return { String } id
*/
export const firstToUpperCase = (str = '') => {
  return str.toLowerCase().replace(/( |^)[a-z]/g, ($1) => $1.toUpperCase())
}

/**
 * @description 防抖
 * @param callback 
 * @param time 
 * @param maxWait 
 * @returns 
 */
export const useDebounce = (callback, time = 1000, maxWait = 5000) =>
  debounce(callback, time, { maxWait, leading: true, trailing: false });

// 获取字典数据
export const getdictData = async (params: number) => {
  const res = await requestWy.get({ url: '/sys/dictinfo/infos/' + params })
  return res?.dtos
}
/**
 * 获取往约枚举
 * name：枚举的enumClazzName
 * field：value字段的取值对象，不一定是code
 * key：label字段的取值对象，不一定是msg
 * @returns
 */
export const getAllWyEnum = async (name: EnumTypeList, field = '', key = '') => {
  let list: { label: string; value: number | null }[] = []
  try {
    const res = await getEnumdefApi({ enumClazzName: name })
    if (res) {
      console.log(res)
      list = res.enumType.map((x) => {
        return {
          label: key ? x[key] : x.msg || x.name,
          value: field ? x[field] : x.code
        }
      })
      return list
    } else {
      return []
    }
  } catch (e) {
    return []
  }
}


export const commonExportExcel = (URL: string, PARAMS: any, METHOD: string, MESSAGE: string = '确认导出') => {
  ElMessageBox.confirm(MESSAGE).then(async () => {
    const r = await exportExcelApi(METHOD, URL, PARAMS)
    if (Number(r.code) === 0) {
      const isPre = window.location.href.includes("adminpre") || window.location.href.includes("/pre");
      ElMessageBox.confirm('导出申请提交成功，是否前往导出页查看?').then(() => {
        window.open((isPre ? '/pre' : '') + '/pc_index.html#modules/busi/exportrecord/show.html', "_blank", "");
      })
    } else {
      ElMessage.error(r.msg)
    }
  })
}

export const loadScripts = (url: string) => {
  return new Promise((resolve) => {
    // 加载js
    const jssrc = document.createElement('script');
    jssrc.onload = () => {
      jssrc.src = url;
      jssrc.onload = () => {
        resolve(true); // 确保加载完成
      };
      document.head.appendChild(jssrc);
    };
    document.head.appendChild(jssrc);
  });
}

/**
 * 裁剪图片
 * @param url 
 * @param size 
 * @returns 
 */
export const tailoring = (url: string, size: number) => {

  if (!url) {
    return '';
  }

  let urlTmp = url;

  if (url.indexOf("http://oss.wangyuedaojia.com/") === -1) {
    return url;
  }

  if (url.indexOf("?") !== -1) {
    urlTmp = url.substring(0, url.lastIndexOf('?'));
  }

  if (url.indexOf("!") != -1) {
    urlTmp = url.substring(0, url.lastIndexOf('!'));
  }

  return urlTmp + "!Tailoring" + size;
}

export const converCitysToTreeList = (list:any[],cityLevels:any[]) => {
  const arr = list.reduce((result, item) => {
    const level = item.bizLevel
    if (!result[level]) {
      result[level] = []
    }
    result[level].push(item)
    return result
  }, {})
  const a = []
  for (let key in arr) {
    console.log(key)
    if(Number(key) || Number(key) === 0){
      a.push({
        label: cityLevels.find(item => item.value === Number(key))?.label || '',
        value: key,
        children: arr[key].map(item => ({
          label: item.name,
          value: item.code,
          leaf: true
        }))
      })
    }
  }
  return a
}
export const formatDate = (d: Date | string | number, f = 'YYYY-MM-DD HH:mm:ss') => {
  if (!d) return ''
  return moment(d).format(f)
}

//  获取最后一天
export const getLastDay = (d: Date | string | number,f = 'YYYY-MM-DD HH:mm:ss') => {
  if (!d) return ''
  return moment(d).endOf('month').format(f)
}

export const jumpToExportPage = (url = '/pc_index.html#modules/busi/exportrecord/show.html') => {
  const isPre = window.location.href.includes("adminpre") || window.location.href.includes("/pre");
        window.open((isPre ? '/pre' : '') + url, "_blank", "");
}

export const formatPercentage = (val: number) => {
  if (!val) return 0
  return ((val * 100).toFixed(2)) + '%'
}

//获取当前时间的去年当时的这一天
export const getBeforeOrAfterYear = (d: Date | string | number,n=1,f = 'YYYY-MM-DD HH:mm:ss') => {
  if (!d) return ''
  return moment(d).subtract(n, 'years').format(f) 
}

type dateInfo = { d: Date | string | number, n?: number, f?: string, calc?: 'add' | 'sub', unit?: 'years' | 'months' | 'days' }
export const calcDate = ({d='',n=1,f='YYYY-MM-DD HH:mm:ss',calc='add',unit='days'}:dateInfo) => {
  if (!d) return ''
  return calc === 'add' ?  moment(d).add(n, unit).format(f) : moment(d).subtract(n, unit).format(f)   
}
//判断手机端还是PC端
export const  IsMobile = ()=> {
    //获取浏览器navigator对象的userAgent属性（浏览器用于HTTP请求的用户代理头的值）
    const info = navigator.userAgent;
    //通过正则表达式的test方法判断是否包含“Mobile”字符串
    const isPhone = /mobile/i.test(info);
    //如果包含“Mobile”（是手机设备）则返回true
    return isPhone
}

export const getAppstoreFields = ({ storeFields, originColumnsData,originMobileColumnsData }) => {
  const appStore = store()
  const initFields = appStore[storeFields]
  const arr = originColumnsData.filter(x => !x.hidden)
  let columns = []
  if (initFields.length) {
    const a = []
    arr.forEach(x => {
      const t = initFields.find(y => y === x.field && !x.hidden)
      if (t) {
      a.push(x)
      }
    })
    columns = a
  } else {
    columns = IsMobile()?originMobileColumnsData: arr
  }
  return columns
}
//秒转换为时分秒
export const secondsToHMS = (seconds: number) => {
    // 计算小时
    const hours = Math.floor(seconds / 3600);
    // 计算剩余秒数
    seconds = seconds % 3600;
    // 计算分钟
    const minutes = Math.floor(seconds / 60);
    // 计算剩余秒数
    const remainingSeconds = Math.floor((seconds % 60));
    return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        remainingSeconds.toString().padStart(2, '0')
    ].join(':');
}

export const timeOverMonths = (startDate: Date | string, endDate: Date | string, diffNumber: number) => {  
    const monthsDiff = moment(endDate).diff(moment(startDate),'months', true);  
    if (Math.abs(monthsDiff) > diffNumber) return true; // 超过diffNumber个月  
    if (Math.abs(monthsDiff) === diffNumber) {  
      // 检查天数  
      return moment(endDate).date() > moment(startDate).date();  
    }  
    return false;  
}

export const formatTimeDuration = (diffMs:number) => {
  if(!diffMs) return ''
    const duration = moment.duration(diffMs);
    // 提取天、时、分、秒
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    
    return `${days}天${hours}小时${minutes}分钟${seconds}秒`;
}
export const IsFloat = (value: number) => {
    return value % 1 !== 0;
}

// json数组分组
export const arrayGroupBy = (array: Record<string, any>[], key: string) => {
  if(!key) return {}
  return array.reduce((result, currentItem) => {
    const groupKey = currentItem[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(currentItem);
    return result;
  }, {});
}
export const showIMiframe = (techId:string) => {
    const ide = (null === localStorage.getItem("ide") || 'null' === localStorage.getItem("ide")) ? "1" : localStorage.getItem("ide");
    const sig = (null === localStorage.getItem("sig") || 'null' === localStorage.getItem("sig")) ? "0" : localStorage.getItem("sig");
    const sdkAppID = (null === localStorage.getItem("imSdkAppid") || 'null' === localStorage.getItem("imSdkAppid")) ? "1400128918" : localStorage.getItem("imSdkAppid");
    let imserver = 'https://im.wangyuedaojia.com/index.html' + '?sig=' + sig + '&sdkAppID=' + sdkAppID + '&ide=' + ide;
    if (techId) {
        imserver += '&techId=' + techId;
    }
    window.open(imserver)
}